import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRouter } from 'next/router';

import type { HashtagType } from '@/utils/hooks/swr/useHashtags';

// Hooks
import useRouterQuery from '@/hooks/utils/useRouterQuery';

import type { DashboardType } from '@/types/dashboardType';
// Types
import type { SocialProfileType } from '@/types/socialProfileType';

// Lib
import reportFormTypesConfig, { REPORT_TYPES } from '@/lib/reports/reportFormTypesConfig';

// Components
import SearchBoxAccount from '@/components/pages/reports/search/SearchBoxAccount';
import SearchBoxCompetitor from '@/components/pages/reports/search/SearchBoxCompetitors';
import SearchBoxDashboard from '@/components/pages/reports/search/SearchBoxDashboard';
import SearchBoxHashtag from '@/components/pages/reports/search/SearchBoxHashtag';
import type {
    ReportFormBodyDataType,
    ReportFormBodyProps
} from '@/components/pages/reports/CreateEditPage/components/ReportForm/ReportFormBody';

type ContentSearchBoxProps = {
    errors: string;
    isLoading: boolean;
    disabled: boolean;
} & (
    | ContentSearchBoxAccountProps
    | ContentSearchBoxDashboardProps
    | ContentSearchBoxHashtagProps
    | ContentSearchBoxCompetitorProps
);

type ContentSearchBoxAccountProps<TList = SocialProfileType[]> = {
    type: typeof REPORT_TYPES.ACCOUNT;
    contentList?: TList;
    onSetList: (items: TList) => void;
};

type ContentSearchBoxDashboardProps<TList = DashboardType[]> = {
    type: typeof REPORT_TYPES.DASHBOARD;
    contentList?: TList;
    onSetList: (items: TList) => void;
};

type ContentSearchBoxHashtagProps<TList = HashtagType[]> = {
    type: typeof REPORT_TYPES.HASHTAG;
    contentList?: TList;
    onSetList: (items: TList) => void;
};

type ContentSearchBoxCompetitorProps<TList = SocialProfileType[]> = {
    type: typeof REPORT_TYPES.COMPETITOR;
    contentList?: TList;
    onSetList: (items: TList) => void;
};

const ContentSearchBox = ({ type, ...restProps }: ContentSearchBoxProps) => {
    const reportFormTypeConfig = reportFormTypesConfig.find((config) => config.type === type);
    const { clearUrlQueriesWithoutReload } = useRouterQuery();
    const router = useRouter();
    const { setValue } = useFormContext<ReportFormBodyDataType>();
    const [list, setList] = useState(restProps.contentList ?? []);

    const preSelectContentItems = (contentItems: ReportFormBodyProps['contentList']) => {
        setList(
            list?.map((item) => {
                if (contentItems.find((contentItem) => item.id === contentItem.id)) {
                    return { ...item, select: true };
                }
                return item;
            })
        );
        setValue('content', contentItems);
        setValue('is_scheduled', 1);
        if (reportFormTypeConfig.formats.some((item) => item === 'pdf')) {
            setValue('report_type', `${reportFormTypeConfig.type}_pdf`);
        }
    };

    const contentIdsQuery = router.query[reportFormTypeConfig.contentIdsQueryName];

    useEffect(() => {
        if (!contentIdsQuery) {
            return;
        }

        let contentIds: string[] = [];

        if (Array.isArray(contentIdsQuery)) {
            contentIds = contentIdsQuery.map((contentId) => contentId);
        } else {
            contentIds = contentIdsQuery.includes(',')
                ? contentIdsQuery.split(',').map((contentId) => contentId)
                : [contentIdsQuery];
        }

        const contentItems = list.filter((item) =>
            contentIds.includes(item.id.toString())
        ) as ReportFormBodyProps['contentList'];

        if (contentItems.length > 0) {
            preSelectContentItems(contentItems);
        }

        clearUrlQueriesWithoutReload();
    }, [contentIdsQuery]);

    switch (type) {
        case REPORT_TYPES.ACCOUNT:
            return <SearchBoxAccount list={list} {...restProps} />;
        case REPORT_TYPES.COMPETITOR:
            return <SearchBoxCompetitor list={list} {...restProps} />;
        case REPORT_TYPES.DASHBOARD:
            return <SearchBoxDashboard list={list} {...restProps} />;
        case REPORT_TYPES.HASHTAG:
            return <SearchBoxHashtag list={list} {...restProps} />;
    }
};

export default ContentSearchBox;
